import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { GoldButton, Section } from "@/components/site/Ui";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Phone, Sparkles, MessageSquareHeart, TrendingUp, UserRound, GraduationCap,
  Briefcase, Baby, Store, Play, Star, ArrowRight, Check, Search, Rocket, Send,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1759393852314-59dc00faeed3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const WHO = [
  { icon: UserRound, t: "Complete beginners", d: "Never made a video? Perfect. This is built for you." },
  { icon: Sparkles, t: "Confused what to post", d: "We turn 'I don't know what to say' into a clear plan." },
  { icon: Briefcase, t: "Busy workers", d: "Simple systems that fit around a full-time job." },
  { icon: GraduationCap, t: "Students", d: "Start young, learn fast, grow an audience early." },
  { icon: Baby, t: "Parents", d: "Create in the small pockets of time you already have." },
  { icon: Store, t: "Small business owners", d: "Get seen by more customers without breaking the bank." },
];

const STEPS = [
  { icon: Search, t: "1. Find your path", d: "We learn about you \u2014 your interests, time and goals \u2014 and pick what fits." },
  { icon: Phone, t: "2. Film on your phone", d: "Simple, step-by-step filming guides. No fancy gear needed." },
  { icon: Rocket, t: "3. Post with a plan", d: "Clickable ideas, titles and tags ready to go, so you never feel stuck." },
  { icon: TrendingUp, t: "4. Grow & earn", d: "Stay consistent and move toward monetisation the honest way." },
];

const WHY = [
  { icon: MessageSquareHeart, t: "Very simple language", d: "No jargon, ever. If a five-year-old couldn't follow it, we rewrite it." },
  { icon: Phone, t: "Phone-only", d: "Everything works with the phone or iPad you already own." },
  { icon: UserRound, t: "Personal 1:1 support", d: "Real human help, made around your life \u2014 not a generic course." },
  { icon: TrendingUp, t: "Real strategies", d: "Practical steps that actually move the needle, minus the hype." },
];

export default function Home() {
  const { settings } = useSite();
  const [products, setProducts] = useState([]);
  const [videos, setVideos] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (settings.youtube_auto_fetch === false) {
      api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
    } else if (Object.keys(settings).length > 0) {
      api.get("/youtube/latest?limit=3").then((r) => setVideos(r.data.map((v, i) => ({ ...v, id: i })))).catch(() =>
        api.get("/videos").then((r) => setVideos(r.data)).catch(() => {}));
    }
  }, [settings]);

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-[#D4AF37]/10 blur-[120px]" />
          <div className="absolute bottom-0 left-0 h-[400px] w-[400px] rounded-full bg-[#D4AF37]/5 blur-[120px]" />
        </div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-14 pb-16 sm:pt-20 sm:pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Eyebrow>Phone-first content creation</Eyebrow>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {settings.hero_title || "Start Creating Content From Your Phone."}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">
              {settings.hero_subtitle}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <GoldButton as="link" to="/services#consultation" data-testid="hero-consultation-btn">
                Book a 1:1 Consultation <ArrowRight size={16} />
              </GoldButton>
              <GoldButton as="link" to="/free-resources" variant="outline" data-testid="hero-guide-btn">
                Get the Free Starter Guide
              </GoldButton>
            </div>
            <div className="mt-8 flex items-center gap-2 text-sm text-white/50">
              <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} size={16} className="text-[#D4AF37] fill-[#D4AF37]" />)}</div>
              Loved by first-time creators
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl bg-gradient-to-tr from-[#D4AF37]/30 to-transparent blur-xl" />
              <img src={HERO_IMG} alt="Creator filming on a phone tripod" loading="eager"
                   className="relative rounded-3xl border border-[#D4AF37]/20 object-cover w-full max-h-[70vh] aspect-[4/5] sm:aspect-[3/4]" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHO */}
      <Section>
        <Reveal><Eyebrow>Who this is for</Eyebrow>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl max-w-2xl">If you've ever felt stuck before pressing record, you're in the right place.</h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {WHO.map((w, i) => (
            <Reveal key={w.t} delay={i * 0.05}>
              <div className="card-surface rounded-2xl p-6 h-full gold-glow-hover">
                <w.icon className="text-[#D4AF37]" strokeWidth={1.5} size={28} />
                <h3 className="mt-4 text-lg font-display">{w.t}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{w.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <div className="relative bg-[#0d0d0d] border-y border-white/5">
        <Section>
          <Reveal><Eyebrow>How it works</Eyebrow>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl">Four simple steps. No overwhelm.</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.08}>
                <div className="relative rounded-2xl border border-white/8 p-6 h-full bg-black/30">
                  <div className="grid place-items-center h-12 w-12 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                    <s.icon className="text-[#D4AF37]" strokeWidth={1.5} size={22} />
                  </div>
                  <h3 className="mt-4 text-lg font-display">{s.t}</h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      {/* SERVICES OVERVIEW */}
      <Section>
        <Reveal><Eyebrow>What we offer</Eyebrow>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl">Services made for real beginners</h2>
        </Reveal>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { t: "1:1 Consultation", d: "Find your content path + a free 7-day video ideas plan.", to: "/services#consultation" },
            { t: "Weekly Check-ins", d: "Ongoing coaching and honest feedback on your videos.", to: "/services#coaching" },
            { t: "YouTube Launch Setup", d: "Set up and optimise your channel together, the right way.", to: "/services#youtube" },
            { t: "Video Editing", d: "Long-form and short-form editing done for you.", to: "/services#editing" },
            { t: "Content Plans", d: "30 clickable video ideas, filmed on your phone.", to: "/content-plans" },
            { t: "Free Resources", d: "Guides, checklists and recommended beginner gear.", to: "/free-resources" },
          ].map((s, i) => (
            <Reveal key={s.t} delay={i * 0.05}>
              <Link to={s.to} className="group card-surface rounded-2xl p-6 h-full flex flex-col gold-glow-hover">
                <h3 className="text-xl font-display group-hover:text-[#D4AF37] transition-colors">{s.t}</h3>
                <p className="mt-2 text-sm text-white/60 flex-1 leading-relaxed">{s.d}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm text-[#D4AF37]">Learn more <ArrowRight size={14} /></span>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* CONTENT PLANS PREVIEW */}
      <div className="relative bg-[#0d0d0d] border-y border-white/5">
        <Section>
          <Reveal><Eyebrow>Our main paid product</Eyebrow>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl">Content Plans that tell you exactly what to post</h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {products.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <div className={`relative rounded-2xl p-7 h-full ${p.best_value ? "border-2 border-[#D4AF37] gold-glow" : "card-surface"}`}>
                  {p.best_value && <span className="absolute -top-3 left-7 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F0D77B] px-3 py-1 text-[11px] font-bold text-[#0A0A0A]">BEST VALUE</span>}
                  <h3 className="text-xl font-display pr-4">{p.name}</h3>
                  <div className="mt-3 flex items-end gap-1">
                    <span className="text-4xl font-display gold-text">£{p.price}</span>
                    <span className="text-white/40 text-sm mb-1">/ {p.videos} videos</span>
                  </div>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed">{p.description}</p>
                  <GoldButton as="link" to="/content-plans" variant={p.best_value ? "solid" : "outline"} className="mt-6 w-full">View details</GoldButton>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      {/* WHY QWERJOH */}
      <Section>
        <Reveal><Eyebrow>Why Qwerjoh</Eyebrow>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl">What you'll get</h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {WHY.map((w, i) => (
            <Reveal key={w.t} delay={i * 0.06}>
              <div className="h-full">
                <w.icon className="text-[#D4AF37]" strokeWidth={1.5} size={28} />
                <h3 className="mt-4 text-lg font-display">{w.t}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{w.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* YOUTUBE VIDEOS */}
      {videos.length > 0 && (
        <div className="relative bg-[#0d0d0d] border-y border-white/5">
          <Section>
            <Reveal className="flex items-end justify-between flex-wrap gap-4">
              <div><Eyebrow>Watch & learn</Eyebrow>
                <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl">Latest from the channel</h2>
              </div>
              <GoldButton as="a" href={settings.youtube_url} target="_blank" rel="noreferrer" variant="outline"><Play size={16} /> Visit YouTube</GoldButton>
            </Reveal>
            <div className="mt-12 grid md:grid-cols-3 gap-5">
              {videos.slice(0, 3).map((v, i) => (
                <Reveal key={v.id ?? v.video_id} delay={i * 0.08}>
                  <div className="card-surface rounded-2xl overflow-hidden gold-glow-hover">
                    <div className="aspect-video bg-black">
                      <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${v.video_id}`}
                              title={v.title} allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                    </div>
                    <div className="p-4"><h3 className="text-sm font-medium">{v.title}</h3></div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Section>
        </div>
      )}

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <Section>
          <Reveal><Eyebrow>Kind words</Eyebrow>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl">Real beginners, real progress</h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <Reveal key={t.id} delay={i * 0.08}>
                <div className="card-surface rounded-2xl p-6 h-full">
                  <div className="flex">{[...Array(t.rating || 5)].map((_, k) => <Star key={k} size={16} className="text-[#D4AF37] fill-[#D4AF37]" />)}</div>
                  <p className="mt-4 text-white/80 italic leading-relaxed">"{t.quote}"</p>
                  <div className="mt-5 text-sm"><span className="text-[#D4AF37] font-medium">{t.name}</span><span className="text-white/40"> · {t.role}</span></div>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>
      )}

      {/* FAQ */}
      <div className="relative bg-[#0d0d0d] border-y border-white/5">
        <Section className="max-w-3xl">
          <Reveal><Eyebrow>Questions</Eyebrow>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl">Frequently asked</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-8">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`i${i}`} className="border-white/10">
                  <AccordionTrigger data-testid={`faq-${i}`} className="text-left hover:text-[#D4AF37] hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-white/60 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </Section>
      </div>

      {/* FINAL CTA */}
      <Section>
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-[#D4AF37]/25 p-10 sm:p-16 text-center grain">
            <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#D4AF37]/10 via-transparent to-[#D4AF37]/5" />
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl max-w-3xl mx-auto leading-tight">Your first video is closer than you think.</h2>
            <p className="mt-4 text-white/70 max-w-xl mx-auto">Book a friendly 1:1 session and walk away with a clear, doable plan made just for you.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <GoldButton as="link" to="/services#consultation"><Send size={16} /> Book a Consultation</GoldButton>
              <GoldButton as="link" to="/content-plans" variant="outline">See Content Plans</GoldButton>
            </div>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
